import { Dispatch } from 'react';
import { useForm, SubmitHandler, UseFormRegister, UseFormWatch } from 'react-hook-form';
import {
  Button,
  MultiSelectDropdown,
  InlineFormInput,
  TimezoneMultiPicker,
  FormsInputDropdown,
} from '@platform/components';
import { useApplicationLocalStorage } from '../../../application/application-hook';
import { SearchStatusEnum, ProfessionalApplicationStatusEnum } from '@platform/client/schemas';
import { AdminProfessionals } from '@platform/client';

export type ProUserFilters = {
  corporateClient: string[] | null;
  cohort: string[] | null;
  applicationStatus: string[] | null;
  timezone: string[] | null;
  matchStatus: string | null;
  maxIntroductions: string | null;
  searchStatus: SearchStatusEnum[] | null;
};

export const ProUserSearchForm = (props: {
  setFilters: Dispatch<React.SetStateAction<ProUserFilters | undefined>>;
}) => {
  const { updateApplicationData, getApplicationData, clearApplicationData } =
    useApplicationLocalStorage<ProUserFilters>('pro-search');
  const { setValue, handleSubmit, register, watch, control, formState, reset } =
    useForm<ProUserFilters>({
      values: { ...getApplicationData() },
    });

  const clearFilters = () => {
    reset({
      corporateClient: null,
      cohort: null,
      applicationStatus: null,
      timezone: null,
      matchStatus: null,
      maxIntroductions: null,
      searchStatus: null,
    });
    clearApplicationData();
    props.setFilters(undefined);
  };

  const onSubmit: SubmitHandler<ProUserFilters> = (data) => {
    updateApplicationData(data);
    props.setFilters(data);
  };

  return (
    <div className='min-w-full flex flex-col gap-4 items-center'>
      <h1>Professional User Search</h1>
      <form className='min-w-full md:min-w-1/2 flex flex-col gap-1'>
        <ClientsCohortSelects
          watch={watch}
          register={register}
          setValue={(field, value) => setValue(field, value)}
        />
        <MultiSelectDropdown
          register={() => register('applicationStatus')}
          label='Application status'
          options={[
            ...Object.values(ProfessionalApplicationStatusEnum).map((status) => ({
              label: status,
            })),
            { label: 'application incomplete' },
          ].sort((a, b) => a.label.localeCompare(b.label))}
          onSelectionChange={(items) => setValue('applicationStatus', items)}
          selectedItems={watch('applicationStatus') || []}
          placeholder='All'
        />
        <MultiSelectDropdown
          register={() => register('searchStatus')}
          label='Search status'
          options={Object.values(SearchStatusEnum).map((status) => ({
            label: status,
            value: status,
          }))}
          onSelectionChange={(items) => setValue('searchStatus', items as SearchStatusEnum[])}
          selectedItems={watch('searchStatus') || []}
          placeholder='All'
        />
        <TimezoneMultiPicker
          placeholder='All'
          timezone={watch('timezone') ?? undefined}
          updateTimezone={(timezone) => setValue('timezone', timezone)}
          register={() => register('timezone')}
          filterable={true}
        />
        <FormsInputDropdown
          name='matchStatus'
          control={control}
          type='default'
          label='Match status'
          items={[
            { text: 'has matched', textValue: 'matched' },
            { text: 'has not matched', textValue: 'not matched' },
          ]}
          placeholder='All'
        />
        <InlineFormInput
          label='Max introductions'
          inputType='number'
          placeholder='Enter number of introductions'
          register={() => register('maxIntroductions')}
          errors={formState.errors.maxIntroductions}
        />
      </form>
      <div className='flex justify-between gap-8 items-center mx-2'>
        <Button
          size='sm'
          variant='tertiary-danger'
          onClick={() => clearFilters()}
          additionalClassnames='w-fit'
        >
          clear filters
        </Button>
        <Button
          size='sm'
          variant='primary'
          onClick={handleSubmit(onSubmit)}
          additionalClassnames='w-fit'
        >
          Preview list
        </Button>
      </div>
    </div>
  );
};

// dynamically generate cohort options based on selected corporate partners with default to all cohorts
const ClientsCohortSelects = (props: {
  setValue: (field: keyof ProUserFilters, value: string[] | null) => void;
  register: UseFormRegister<ProUserFilters>;
  watch: UseFormWatch<ProUserFilters>;
}) => {
  const { data: cohortsByClient } = AdminProfessionals.useGetCohortsByClients();

  const clientOptionsRaw = cohortsByClient?.clients.map((client) => ({
    label: client.name,
    value: client.name,
  }));
  const clientOptions = clientOptionsRaw?.sort((a, b) => a.label.localeCompare(b.label));
  const selectedPartnerIds = props.watch('corporateClient') || [];

  const allCohorts =
    cohortsByClient?.clients
      .flatMap((client) => client.cohorts)
      .map((cohort) => {
        return { value: cohort.name };
      }) || [];
  const cohortOptionsByClient =
    selectedPartnerIds.length > 0 ?
      cohortsByClient?.clients
        .filter((client) => selectedPartnerIds.includes(client.name))
        .flatMap((client) => client.cohorts)
        .map((cohort) => ({ label: cohort.name, value: cohort.id }))
    : allCohorts.map((cohort) => ({ label: cohort.value, value: cohort.value }));
  const cohortOptions = cohortOptionsByClient?.sort((a, b) => a.label.localeCompare(b.label));

  return (
    <>
      <MultiSelectDropdown
        register={() => props.register('corporateClient')}
        label='Corporate client'
        options={[...(clientOptions ?? []), { label: 'None', value: 'None' }]}
        onSelectionChange={(items) => props.setValue('corporateClient', items)}
        selectedItems={selectedPartnerIds}
        placeholder='All'
      />
      <MultiSelectDropdown
        register={() => props.register('cohort')}
        label='Cohort'
        options={[...(cohortOptions ?? []), { label: 'None', value: 'None' }]}
        onSelectionChange={(items) => props.setValue('cohort', items)}
        selectedItems={props.watch('cohort') || []}
        placeholder='All'
        filterable={true}
      />
    </>
  );
};
